import React, { useState } from 'react';
import { User } from './types/auth';
import { usePredictionMarket } from './hooks/usePredictionMarket';
import TxToast from './components/common/TxToast';

interface CreateMarketProps {
  user: User | null;
}

const ASSET_TYPES = ['Stock', 'Crypto', 'FX', 'Commodity', 'Index', 'Other'];
const SETTLEMENT_METHODS = ['Price above strike', 'Price below strike', 'Yes/No event'];

export default function CreateMarket({ user }: CreateMarketProps) {
  const { createMarket, tx, resetTx } = usePredictionMarket();

  const [ticker, setTicker] = useState('');
  const [assetType, setAssetType] = useState(ASSET_TYPES[0]);
  const [strikePrice, setStrikePrice] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [expirationTime, setExpirationTime] = useState('23:59');
  const [settlementMethod, setSettlementMethod] = useState(SETTLEMENT_METHODS[0]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (!user?.isAdmin) {
    return (
      <div className="page-container">
        <div className="access-denied">
          <span className="access-denied-icon">🔒</span>
          <h2>Admin Access Required</h2>
          <p>Only administrators can create new prediction markets.</p>
        </div>
      </div>
    );
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!ticker.trim()) errs.ticker = 'Ticker is required.';
    if (!strikePrice || isNaN(Number(strikePrice)) || Number(strikePrice) <= 0)
      errs.strikePrice = 'Enter a valid positive strike price.';
    if (!expirationDate) errs.expirationDate = 'Expiration date is required.';
    else {
      const deadlineMs = new Date(`${expirationDate}T${expirationTime}`).getTime();
      if (deadlineMs <= Date.now()) errs.expirationDate = 'Expiration must be in the future.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    const deadlineTimestamp = Math.floor(
      new Date(`${expirationDate}T${expirationTime}`).getTime() / 1000,
    );
    const question = `Will ${ticker.toUpperCase()} (${assetType}) ${settlementMethod.toLowerCase()} at ${strikePrice}?`;
    const success = await createMarket(question, deadlineTimestamp);
    if (success) {
      setTicker('');
      setStrikePrice('');
      setExpirationDate('');
      setExpirationTime('23:59');
      setErrors({});
    }
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2 className="page-title">➕ Create Prediction Market</h2>
        <p className="page-subtitle">
          Define a new market. Only admins can create markets.
        </p>
      </div>

      <TxToast tx={tx} onDismiss={resetTx} />

      <form className="market-form" onSubmit={handleSubmit} noValidate>
        {/* Ticker */}
        <div className="form-group">
          <label className="form-label" htmlFor="cm-ticker">
            Ticker / Symbol <span className="required">*</span>
          </label>
          <input
            id="cm-ticker"
            className={`form-input${errors.ticker ? ' form-input-error' : ''}`}
            type="text"
            placeholder="e.g. BTC, AAPL, EUR/USD"
            value={ticker}
            onChange={e => setTicker(e.target.value)}
            required
          />
          {errors.ticker && <p className="form-error">{errors.ticker}</p>}
        </div>

        {/* Asset Type */}
        <div className="form-group">
          <label className="form-label" htmlFor="cm-asset-type">
            Asset Type <span className="required">*</span>
          </label>
          <select
            id="cm-asset-type"
            className="form-input form-select"
            value={assetType}
            onChange={e => setAssetType(e.target.value)}
          >
            {ASSET_TYPES.map(t => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        {/* Strike Price */}
        <div className="form-group">
          <label className="form-label" htmlFor="cm-strike">
            Strike Price (USD) <span className="required">*</span>
          </label>
          <input
            id="cm-strike"
            className={`form-input${errors.strikePrice ? ' form-input-error' : ''}`}
            type="number"
            min="0"
            step="any"
            placeholder="e.g. 50000"
            value={strikePrice}
            onChange={e => setStrikePrice(e.target.value)}
            required
          />
          {errors.strikePrice && <p className="form-error">{errors.strikePrice}</p>}
        </div>

        {/* Expiration */}
        <div className="form-row">
          <div className="form-group form-group-flex">
            <label className="form-label" htmlFor="cm-expiry-date">
              Expiration Date <span className="required">*</span>
            </label>
            <input
              id="cm-expiry-date"
              className={`form-input${errors.expirationDate ? ' form-input-error' : ''}`}
              type="date"
              value={expirationDate}
              onChange={e => setExpirationDate(e.target.value)}
              required
            />
            {errors.expirationDate && (
              <p className="form-error">{errors.expirationDate}</p>
            )}
          </div>
          <div className="form-group form-group-flex">
            <label className="form-label" htmlFor="cm-expiry-time">
              Expiration Time (UTC)
            </label>
            <input
              id="cm-expiry-time"
              className="form-input"
              type="time"
              value={expirationTime}
              onChange={e => setExpirationTime(e.target.value)}
            />
          </div>
        </div>

        {/* Settlement Method */}
        <div className="form-group">
          <label className="form-label" htmlFor="cm-settlement">
            Settlement Method <span className="required">*</span>
          </label>
          <select
            id="cm-settlement"
            className="form-input form-select"
            value={settlementMethod}
            onChange={e => setSettlementMethod(e.target.value)}
          >
            {SETTLEMENT_METHODS.map(m => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>

        {/* Preview */}
        {ticker && strikePrice && (
          <div className="form-preview">
            <p className="form-preview-label">Market question preview:</p>
            <p className="form-preview-text">
              Will {ticker.toUpperCase()} ({assetType}){' '}
              {settlementMethod.toLowerCase()} at {strikePrice}?
            </p>
          </div>
        )}

        <button
          type="submit"
          className="btn-primary btn-full"
          disabled={tx.status === 'pending'}
        >
          {tx.status === 'pending' ? '⏳ Submitting…' : '🚀 Create Market'}
        </button>
      </form>
    </div>
  );
}
